import { useEffect, useState } from 'react';
import {
  DESK_PAYMENT_PENDING_HORIZON_MS,
  DeskPayment,
  deskPaymentCreatedAt
} from '@/modules/billing';
import { useDeskPaymentFollow } from './useDeskPaymentFollow';

/**
 * Slower than the dialog's own poll: nobody is watching this payment land, so following it
 * matches the cadence of the patient's payment list rather than a screen at the desk.
 */
const POLLING_INTERVAL_MS = 5000;

/**
 * Keeps one pending desk payment moving with no dialog open on it. Webhooks are wired since 3c
 * and can advance a payment on their own, but the patient's payment list is a plain DB read that
 * only shows what has already landed — so without this a payment whose dialog was closed on
 * "leave running" could sit tapped-but-uncaptured until a webhook happens to arrive or the
 * backend sweeper closes it out, with the row still offering to resume a payment the patient
 * already made.
 *
 * Bounded by the payment's own {@link DESK_PAYMENT_PENDING_HORIZON_MS} horizon, on a timer of
 * its own rather than on a render: the host that would drop this component stops rendering in
 * an unfocused tab, which is exactly the tab this is here to keep working.
 *
 * Headless: it exists for its poll alone — what the outcome invalidates is dispatched from
 * `deskPaymentRegistry.listener.ts`, which also drops the finished payment from the registry
 * and so unmounts this. Mounted by the app-wide `DeskPaymentRegistryRefreshers` (see
 * `deskPaymentRegistry.slice.ts`).
 */
export const DeskPaymentRefresher = ({ payment }: { payment: DeskPayment }) => {
  const deadline =
    deskPaymentCreatedAt(payment) + DESK_PAYMENT_PENDING_HORIZON_MS;
  const [pastDeadline, setPastDeadline] = useState(() => Date.now() > deadline);

  useEffect(() => {
    const delay = deadline - Date.now();
    if (delay <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setPastDeadline(true), delay);
    return () => clearTimeout(timer);
  }, [deadline]);

  useDeskPaymentFollow(payment, {
    pollingInterval: POLLING_INTERVAL_MS,
    paused: pastDeadline
  });
  return null;
};
